import { Fragment } from 'react/jsx-runtime'
import { Link, useSearchParams } from 'react-router'
import {
  RadioGroup,
  RadioProps,
  ScrollShadow,
  Skeleton,
  useRadio,
  VisuallyHidden
} from '@heroui/react'
import clsx from 'clsx'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { usePostHog } from '@posthog/react'
import { Trans, useTranslation } from 'react-i18next'
import {
  ReturnsExchangeOptionsExchangeFlowEnum,
  ReturnsExchangeOptionsResponseOneOnOneVariantInfo
} from '@itsrever/rever-api'
import { useTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { hasVariantStock } from '@/app/full-catalog/helpers/has-variant-stock'
import { DEFAULT_COUNTRY_CODE } from '@/app/full-catalog/helpers/constants'
import { storeMenuQuery } from '@/hooks/use-store-menu'
import { ApiReturnedItemSubproduct } from '@/redux/return/return-slice'
import { storeProductsQuery } from '@/hooks/use-store-products'
import { NavArrowRightIcon } from '@/ui/icons/nav-arrow-right'
import { FlowPrimaryButton, FlowSecondaryButton } from './flow-buttons'

const SIZE_RELATED_RETURN_REASONS = [
  'WRONG_SIZE_TOO_SMALL',
  'WRONG_SIZE_TOO_BIG',
  'WRONG_SIZE_TOO_SHORT',
  'WRONG_SIZE_TOO_LONG'
]

const schema = z.object({
  platform_id: z.string()
})

type VariantData = z.infer<typeof schema>

interface ItemExchangeProps {
  variants: ReturnsExchangeOptionsResponseOneOnOneVariantInfo[]
  exchangeFlow: ReturnsExchangeOptionsExchangeFlowEnum
  returnedItem: ApiReturnedItemSubproduct
  creditAmount: number
  onContinue: (data?: ReturnsExchangeOptionsResponseOneOnOneVariantInfo) => void
  onClose: () => void
}

export const ItemExchange = ({
  variants,
  returnedItem,
  exchangeFlow,
  creditAmount,
  onContinue,
  onClose
}: ItemExchangeProps) => {
  const { translate } = useTranslate()

  const { t } = useTranslation()

  const posthog = usePostHog()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  const titleClass = clsx('text-base', {
    'font-medium': !isHoff && !isMisako,
    'font-normal': isHoff,
    'font-[300] tracking-wider': isMisako
  })

  const { handleSubmit, control } = useForm<VariantData>({
    resolver: zodResolver(schema),
    defaultValues: {
      platform_id: ''
    }
  })

  const platformId = useWatch({ control, name: 'platform_id' })

  const onVariantSelect = (data: VariantData) => {
    const selectedVariant = variants.find(
      (variant) => variant.platform_id === data.platform_id
    )

    posthog?.capture('Exchange OneOnOne Selected', {
      checkout_id: checkoutId
    })

    onContinue(selectedVariant)
  }

  return (
    <form
      className="flex flex-1 flex-col gap-5 overflow-hidden p-4 md:p-5"
      onSubmit={handleSubmit(onVariantSelect)}
    >
      <div className="flex h-full min-h-0 flex-1 animate-fade-in flex-col gap-6">
        <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
          <p
            id="variant-options"
            className={titleClass}
            dangerouslySetInnerHTML={{
              __html: SIZE_RELATED_RETURN_REASONS.includes(
                returnedItem.item.product_return_reason
              )
                ? t(
                    `initial_selection.item_variant_exchange_${returnedItem.item.product_return_reason}`
                  )
                : translate('item_selection_exchange_default_title')
            }}
          />
          <ScrollShadow className="h-full overflow-y-auto" size={80}>
            <Controller
              control={control}
              name="platform_id"
              render={({ field: { onChange, value } }) => (
                <RadioGroup
                  onValueChange={onChange}
                  value={value}
                  aria-labelledby="variant-options"
                  orientation="vertical"
                  classNames={{
                    wrapper: 'gap-0.5 flex-nowrap'
                  }}
                >
                  {variants.map((variant) => {
                    return (
                      <VariantExchangeButton
                        value={variant.platform_id!}
                        key={variant.platform_id}
                        variant={variant}
                        returnedItemQuantity={returnedItem.item.quantity}
                      />
                    )
                  })}
                </RadioGroup>
              )}
            />
          </ScrollShadow>
        </div>
      </div>
      <div className="space-y-4">
        {exchangeFlow === ReturnsExchangeOptionsExchangeFlowEnum.Full && (
          <FullCatalogAction
            returnedItem={returnedItem}
            creditAmount={creditAmount}
            onClose={onClose}
          />
        )}
        <div className="mt-auto flex flex-col gap-2">
          <FlowPrimaryButton type="submit" isDisabled={platformId.length === 0}>
            <span
              dangerouslySetInnerHTML={{
                __html: translate(
                  `item_selection_exchange_primary_action_button`
                )
              }}
            />
          </FlowPrimaryButton>
          <FlowSecondaryButton onPress={() => onContinue()}>
            <span
              dangerouslySetInnerHTML={{
                __html: translate(
                  `item_selection_exchange_secondary_action_button`
                )
              }}
            />
          </FlowSecondaryButton>
        </div>
      </div>
    </form>
  )
}

interface VariantExchangeButtonProps extends RadioProps {
  variant: ReturnsExchangeOptionsResponseOneOnOneVariantInfo
  returnedItemQuantity: number
}

const VariantExchangeButton = ({
  variant,
  returnedItemQuantity,
  ...props
}: VariantExchangeButtonProps) => {
  const {
    Component,
    getBaseProps,
    getWrapperProps,
    getInputProps,
    getLabelProps,
    getLabelWrapperProps,
    getControlProps
  } = useRadio(props)

  const { t } = useTranslation()

  const isInStore = useAppSelector(
    (store) => store.settingsApi.settings.response.in_store_settings?.is_enabled
  )

  const group = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const isHoff = group === 'hoff'
  const isMisako = group === 'misako'

  const labelClass = clsx('line-clamp-2 text-center tabular-nums', {
    'text-sm font-medium group-data-[selected=true]:font-semibold':
      !isHoff && !isMisako,
    'text-sm font-normal': isHoff,
    'text-xs font-[300] uppercase tracking-wider': isMisako
  })

  const hasStock =
    variant.is_available && hasVariantStock(variant, returnedItemQuantity)

  return (
    <Component
      {...getBaseProps()}
      data-stock={!hasStock}
      className="group flex min-h-12 min-w-20 cursor-pointer items-center justify-center rounded-md p-2.5 hover:bg-neutral-50 data-[stock=true]:cursor-default data-[selected=true]:border-neutral-800 data-[selected=true]:bg-neutral-200/40 data-[stock=true]:opacity-50"
    >
      <VisuallyHidden>
        <input {...getInputProps()} disabled={!hasStock} />
      </VisuallyHidden>
      <VisuallyHidden>
        <span {...getWrapperProps()}>
          <span {...getControlProps()} />
        </span>
      </VisuallyHidden>
      <div
        {...getLabelWrapperProps()}
        className="flex flex-col items-center justify-center gap-1 px-0"
      >
        <span {...getLabelProps()} className={labelClass}>
          {variant.name}
        </span>
        {isInStore && hasStock ? (
          <span className="text-xs">{t('full_catalog.in_store_label')}</span>
        ) : null}
      </div>
    </Component>
  )
}

interface FullCatalogActionProps {
  returnedItem: ApiReturnedItemSubproduct
  creditAmount: number
  onClose: () => void
}

export const FullCatalogAction = ({
  returnedItem,
  creditAmount,
  onClose
}: FullCatalogActionProps) => {
  const {
    i18n: { language },
    t
  } = useTranslation()

  const posthog = usePostHog()

  const [searchParams] = useSearchParams()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const countryCode =
    useAppSelector((store) => store.return.returnAddress.country_code) ??
    DEFAULT_COUNTRY_CODE

  const orderCurrency = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )?.currency

  const orderReference = useAppSelector(
    (store) =>
      store.returnsApi.order.response?.order?.identification
        ?.customer_printed_order_id
  )

  const { isFetched: isMenuFetched, data: menu } = useQuery({
    ...storeMenuQuery({
      ecommerceId,
      language,
      countryCode,
      orderReference
    })
  })

  const { data, status } = useInfiniteQuery({
    ...storeProductsQuery({
      ecommerceId,
      language,
      countryCode,
      orderCurrency,
      resourceId: menu?.items?.[0] ? menu?.items?.[0].resource_id : undefined,
      resourceType: menu?.items?.[0]
        ? menu?.items?.[0].resource_type
        : undefined,
      orderReference
    }),
    enabled: isMenuFetched
  })

  const createQueryString = () => {
    const params = new URLSearchParams(searchParams.toString())

    params.set('catalog', 'true')

    return params.toString()
  }

  const onFullCatalogClick = () => {
    posthog.capture('Exchange FullCatalog Clicked', {
      element_type: 'cta_banner',
      checkout_id: checkoutId
    })

    onClose()
  }

  return (
    <div className="group relative flex animate-fade-in items-center gap-4 rounded-md bg-neutral-100/70 px-3 py-4 transition-background hover:bg-neutral-100 md:px-4 md:py-5">
      {status === 'pending' && (
        <div className="flex items-center -space-x-3">
          <Skeleton className="size-8 rounded-xl md:size-11" />
          <Skeleton className="size-8 rounded-xl md:size-11" />
          <Skeleton className="size-8 rounded-xl md:size-11" />
        </div>
      )}
      {status === 'success' && (
        <ul className="flex items-center -space-x-3">
          {data?.pages.slice(0, 1).map((page) => (
            <Fragment key={page.page_info?.end_cursor}>
              {page.products?.slice(0, 3).map((product) => (
                <li
                  key={product.id}
                  className="size-8 overflow-hidden rounded-xl border-1.5 border-solid border-white bg-white shadow-sm outline outline-1 outline-neutral-200/60 sm:size-11"
                >
                  {product.featured_image_url ? (
                    <img
                      src={product.featured_image_url}
                      alt={product.title}
                      width={40}
                      height={40}
                      className="aspect-square size-full rounded-xl object-cover"
                    />
                  ) : null}
                </li>
              ))}
            </Fragment>
          ))}
        </ul>
      )}
      <div className="-mt-0.5 space-y-0.5">
        <p className="text-sm font-medium">
          {t('initial_selection.full_catalog_action_title')}
        </p>
        <p className="text-xs font-normal leading-4 tracking-wide text-neutral-600 [text-wrap:balance]">
          {creditAmount > 0 ? (
            <Trans
              i18nKey="initial_selection.full_catalog_action_description"
              values={{
                creditAmount: creditAmount.toLocaleString(language, {
                  style: 'currency',
                  currency: moneyFormat?.currency
                })
              }}
              components={{
                strong: <strong className="font-medium text-neutral-900" />
              }}
            />
          ) : (
            t('initial_selection.full_catalog_action_description_no_credit')
          )}
        </p>
      </div>
      <div className="ml-auto flex size-7 shrink-0 items-center justify-center rounded-full bg-neutral-200/50 group-hover:bg-neutral-200/80 sm:size-8">
        <Link
          to={{
            search: '?' + createQueryString()
          }}
          state={{ returnedItem }}
          onClick={onFullCatalogClick}
          className="before:absolute before:inset-0 before:z-10"
          aria-label={t('initial_selection.full_catalog_action_button')}
        >
          <NavArrowRightIcon className="size-4 sm:size-5" />
        </Link>
      </div>
    </div>
  )
}
